<script lang="ts" setup>
import { NSelect, NSpace } from 'naive-ui'
import { ref } from 'vue'
import Clipboard from 'clipboard'
import { createInviteLink } from '@/features/personal-center/api/company'
import { enCrypto } from '@/utils/crypto'
import { getPosthogPath } from '@/utils/tool'
import { useWorkspaceStore } from '@/store/modules/workspace'

const props = defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits(['update:modelValue'])

const showModal = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const effectiveTime = ref(2)
const inviteUrl = ref('')
const copyRef = ref<HTMLElement | null>(null)

const effectiveTimeOptions = [
  { label: '1天', value: 1 },
  { label: '7天', value: 2 },
  { label: '14天', value: 3 },
  { label: '长期有效', value: 0 },
]

const workspaceStore = useWorkspaceStore()
const currentCompanyData = computed(() => workspaceStore.currentWorkspace)

function _resetState() {
  effectiveTime.value = 2
}

function onUpdateValue(value: string) {
  // handle value update
}

function querystringToJson(queryString) {
  const regex = /([^&=]+)=([^&]+)/g
  let match
  const json = {}
  while ((match = regex.exec(queryString)) !== null) {
    json[decodeURIComponent(match[1])] = decodeURIComponent(match[2])
  }
  return json
}

function generateInviteUrl(event) {
  const clipboard = new Clipboard('.copyUrl')

  clipboard.on('success', (e: any) => {
    e.clearSelection()
    clipboard.destroy()
    window.$message?.success('链接复制成功')
  })

  clipboard.on('error', (e: any) => {
    clipboard.destroy()
    window.$message?.success('链接复制失败，该浏览器不支持自动复制')
  })

  clipboard.onClick(event)
}

function onConfirm() {
  createInviteLink({
    enterpriseId: currentCompanyData.value?.workspaceId as number,
    ageLimit: effectiveTime.value
  }).then((res) => {
    try {
      const origin = window.location.origin
      const json = querystringToJson(res)
      const encodeCode = enCrypto({
        enterpriseName: currentCompanyData.value?.workspaceName,
        type: 'company',
        enterpriseId: currentCompanyData.value?.workspaceId as number,
        ...json,
      })
      inviteUrl.value = `${origin}/company-invitation?invitationCode=${encodeCode}`

      nextTick(() => {
        copyRef.value && copyRef.value.click()
      })
    } catch (error) {
      console.log('邀请链接生成失败', error)
    }
  })

  closeModal()
}

function closeModal() {
  showModal.value = false
  _resetState()
}
</script>

<template>
  <CompanyBasicModal v-model="showModal" class="company-limit-point-modal-content" title="加入企业邀请链接"
    show-header-bottom-border>
    <div class="effective-time-item overflow-hidden">
      <span class="label">企业名称：</span>
      <span class="ellipsis" :title="currentCompanyData?.workspaceName">{{ currentCompanyData?.workspaceName }}</span>
    </div>
    <div class="effective-time-item">
      <span class="label">有效时长：</span>
      <NSelect v-model:value="effectiveTime" placeholder="请输入手机号码" filterable :options="effectiveTimeOptions"
        @update:value="onUpdateValue" />
    </div>
    <div class="effective-time-tips">
      注意：需要企业管理员审核通过后才能加入企业。
    </div>
    <template #footer>
      <NSpace justify="end" :size="12">
        <button class="footer-btn button-gradient copy-link-btn"
          :data-ph-capture-attribute-page-actions="getPosthogPath(['企业成员', '加入企业邀请链接', '复制链接'])" @click="onConfirm">
          复制链接
        </button>
      </NSpace>
      <span ref="copyRef" class="copyUrl" :data-clipboard-text="inviteUrl" @click="generateInviteUrl($event)" />
    </template>
  </CompanyBasicModal>
</template>

<style lang="scss" scoped>
// 加入企业邀请链接
.effective-time-item {
  display: flex;
  align-items: center;
  max-width: 500px;

  &+.effective-time-item {
    margin-top: 20px;
  }

  .label {
    flex: 0 0 70px;
    width: 70px;
    margin-right: 4px;
  }
}

.effective-time-tips {
  margin-top: 12px;
  padding: 8px 12px;
  color: #C8831D;
  background: rgba(255, 250, 232, 0.91);
  border-radius: 4px 4px 4px 4px;
  font-size: 12px;
}
</style>